import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isNil } from 'lodash';
import RulerMarkerOverlayComponent from './RulerMarkerOverlayComponent';
import appTheme from '../../common/theme/appTheme.json';
import { OVERLAY_Z_INDEX, positionRulerOverlay } from './RulerLegOverlayContainer';

export interface RulerMarkerOverlayContainerProps {
  marker: google.maps.Marker;
  map: google.maps.Map;
}

const RulerMarkerOverlayContainer: React.FC<RulerMarkerOverlayContainerProps> = (
  props: RulerMarkerOverlayContainerProps,
) => {
  const { marker, map } = props;

  const containerElement = useRef<HTMLDivElement | null>(document.createElement('div'));
  const mouseOverEventListener = useRef<google.maps.MapsEventListener | null>(null);
  const mouseOutEventListener = useRef<google.maps.MapsEventListener | null>(null);
  const mouseDownEventListener = useRef<google.maps.MapsEventListener | null>(null);
  const mouseDragStartEventListener = useRef<google.maps.MapsEventListener | null>(null);
  const mouseDragEndEventListener = useRef<google.maps.MapsEventListener | null>(null);
  const markerPositionChangedEventListener = useRef<google.maps.MapsEventListener | null>(null);
  const mapMouseMoveEventListener = useRef<google.maps.MapsEventListener | null>(null);

  const [overlayView] = useState(new google.maps.OverlayView());
  const [visible, setVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [location, setLocation] = useState<google.maps.LatLng | null>(marker.getPosition() || null);
  const tempMarker = useMemo(() => {
    return new google.maps.Marker({
      opacity: 0.75,
      draggable: false,
      clickable: false,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: appTheme.mptLightBlue, // Inner circle color
        fillOpacity: 1,
        strokeColor: appTheme.mptLightBlue, // Outer circle color
        strokeWeight: 3, // Outer circle width
        scale: 10, // Circle size
      },
    });
  }, []);

  const handleMarkerMouseOver = useCallback(() => {
    // Display overlay if user isn't dragging and their mouse is over the marker
    if (!isDragging) {
      setVisible(true);
      tempMarker.setPosition(location);
      tempMarker.setMap(map);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, location]);

  const handleMarkerMouseOut = useCallback(() => {
    // Hide overlay if user's mouse left the marker
    setVisible(false);
    tempMarker.setMap(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkerMouseDown = useCallback(() => {
    // Hide overlay if the user clicks on the marker after mouseover
    setVisible(false);
    tempMarker.setMap(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMarkerMouseDragStart = useCallback(() => {
    // Set isDragging to true so the overlay doesn't display while dragging
    // (mouseover event fires as the user drags the marker)
    setIsDragging(true);
  }, []);

  const handleMarkerMouseDragEnd = useCallback(() => {
    // Set isDragging to false and display the overlay at the new location
    setIsDragging(false);
    setLocation(marker.getPosition() || null);
    setVisible(true);
    // Update the temporary marker to the new location and ensure it's added to the map
    tempMarker.setPosition(marker.getPosition());
    tempMarker.setMap(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marker]);

  const handleMarkerPositionChange = useCallback(() => {
    // Handles the case where the marker is auto-updated to match the new position of an aircraft
    if (!isDragging) {
      setLocation(marker.getPosition() || null);
      tempMarker.setPosition(marker.getPosition());
    }
  }, [marker, tempMarker, isDragging]);

  // Prevents the overlay and tempMarker from displaying when the cursor is outside the bounds of the marker
  const handleMapMouseMove = useCallback(
    event => {
      const projection = map.getProjection();
      if (!isNil(projection) && visible) {
        const latLng = event.latLng;

        const markerPosition = marker.getPosition();
        if (!isNil(markerPosition) && !isNil(map.getZoom())) {
          const markerPoint = projection.fromLatLngToPoint(markerPosition); // Marker position in map's coordinate system
          const scale = Math.pow(2, map.getZoom() as number); // Scale value based on zoom level
          const pixelOffset = 10; // Offset in pixels

          // Offset value in map's coordinate system (not pixels) based on zoom level and map projection
          const offset = pixelOffset / scale;

          // Calculate the southwest and northeast points of the marker with the offset (in map's coordinate system)
          const swPoint = new google.maps.Point(markerPoint.x - offset, markerPoint.y + offset);
          const nePoint = new google.maps.Point(markerPoint.x + offset, markerPoint.y - offset);

          // Convert the points to LatLng values
          const swLatLng = projection.fromPointToLatLng(swPoint);
          const neLatLng = projection.fromPointToLatLng(nePoint);

          // Create a LatLngBounds object to check if the cursor is within the bounds of the marker
          const markerBounds = new google.maps.LatLngBounds(swLatLng, neLatLng);

          // If the cursor is not within the bounds of the marker, trigger the marker mouseout event
          if (latLng && !markerBounds.contains(latLng)) {
            handleMarkerMouseOut();
          }
        }
      }
    },
    [map, marker, handleMarkerMouseOut, visible],
  );

  // Memoize the draw method to change when props change
  const drawCallback = useCallback((): void => {
    const overlayProjection = overlayView.getProjection();
    if (containerElement.current && overlayProjection && !isNil(location)) {
      // Get the pixel position of the display location (i.e. where the mouse is hovering)
      const point = overlayProjection.fromLatLngToDivPixel(location);

      // Temporarily set display to block to measure dimensions of the overlay
      containerElement.current.style.display = 'block';
      const { width, height } = containerElement.current.getBoundingClientRect();

      // Calculate the left and top positions of the overlay
      const { left, top } = positionRulerOverlay(point, width, height, map, overlayProjection);

      containerElement.current.style.left = `${left}px`;
      containerElement.current.style.top = `${top}px`;
      containerElement.current.style.zIndex = OVERLAY_Z_INDEX.toString();
      containerElement.current.style.display = visible ? 'block' : 'none';
    }
  }, [overlayView, visible, map, location]);

  const onAddCallback = useCallback((): void => {
    if (containerElement.current) {
      containerElement.current.style.position = 'absolute';
      containerElement.current.setAttribute('data-test', `ruler__leg__overlay__${marker.getZIndex()}`);

      const panes = overlayView.getPanes();
      if (panes) {
        panes.floatPane.appendChild(containerElement.current);
      }
    }
  }, [overlayView, marker]);

  // Remove all event listeners
  const removeMarkerEventListeners = useCallback(() => {
    if (mouseOverEventListener.current) {
      mouseOverEventListener.current.remove();
    }

    if (mouseOutEventListener.current) {
      mouseOutEventListener.current.remove();
    }

    if (mouseDownEventListener.current) {
      mouseDownEventListener.current.remove();
    }

    if (mouseDragStartEventListener.current) {
      mouseDragStartEventListener.current.remove();
    }

    if (mouseDragEndEventListener.current) {
      mouseDragEndEventListener.current.remove();
    }

    if (markerPositionChangedEventListener.current) {
      markerPositionChangedEventListener.current.remove();
    }

    if (mapMouseMoveEventListener.current) {
      mapMouseMoveEventListener.current.remove();
    }
  }, []);

  const onRemove = useCallback((): void => {
    if (containerElement?.current && containerElement.current.parentNode) {
      containerElement.current.parentNode.removeChild(containerElement.current);
    }
    containerElement.current = null;

    // Remove marker event listeners
    removeMarkerEventListeners();
  }, [removeMarkerEventListeners]);

  const overlayComponent: JSX.Element | null = useMemo(() => {
    // Return the overlay component
    return !isNil(location) ? <RulerMarkerOverlayComponent latLng={location} /> : null;
  }, [location]);

  // Set up overlayView
  useEffect(() => {
    overlayView.draw = drawCallback;
    overlayView.onAdd = onAddCallback;
    overlayView.onRemove = onRemove;
    overlayView.setMap(map);

    return () => {
      // Remove the overlay view from the map
      overlayView.setMap(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add event listeners for marker
  useEffect(() => {
    mouseOverEventListener.current = marker.addListener('mouseover', handleMarkerMouseOver);
    mouseOutEventListener.current = marker.addListener('mouseout', handleMarkerMouseOut);
    mouseDownEventListener.current = marker.addListener('mousedown', handleMarkerMouseDown);
    mouseDragStartEventListener.current = marker.addListener('dragstart', handleMarkerMouseDragStart);
    mouseDragEndEventListener.current = marker.addListener('dragend', handleMarkerMouseDragEnd);
    markerPositionChangedEventListener.current = marker.addListener('position_changed', handleMarkerPositionChange);
    mapMouseMoveEventListener.current = map.addListener('mousemove', handleMapMouseMove);

    return () => {
      removeMarkerEventListeners();
    };
  }, [
    marker,
    map,
    handleMarkerMouseOver,
    handleMarkerMouseOut,
    handleMarkerMouseDown,
    handleMarkerMouseDragStart,
    handleMarkerMouseDragEnd,
    handleMarkerPositionChange,
    removeMarkerEventListeners,
    handleMapMouseMove,
  ]);

  // Redraw the overlay when the drawCallback changes
  useEffect(() => {
    overlayView.draw = drawCallback;
    overlayView.draw();
  }, [drawCallback, overlayView]);

  // render is used to keep this component in the virtual dom. The actual
  // DOM will be set in onAdd() for the google.maps.OverlayView()
  return containerElement.current ? createPortal(overlayComponent, containerElement.current) : null;
};

export default RulerMarkerOverlayContainer;
